import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from './FormContext';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { UniversalFormProps } from './types';

const sanitizeKey = (label: string) => label.replace(/[^a-zA-Z0-9]/g, '_');

const UniversalForm: React.FC<UniversalFormProps> = ({
  config,
  formKey,
  hide,
}) => {
  const { formData, updateField, iniJsonData, basin, liquefaction } =
    useFormContext();

  const selectedOption = useMemo(() => {
    if (formKey && config.key) {
      return (
        formData[formKey]?.[config.key] ||
        (config.options.length > 0
          ? typeof config.options[0] === 'string'
            ? config.options[0]
            : config.options[0].value
          : 'default')
      );
    }
    return 'default';
  }, [formData, formKey, config]);

  const [formCompleted, setFormCompleted] = useState<boolean>(false);

  const checkFormCompletion = useCallback(() => {
    const currentFormData = formData[formKey] || {};
    const allFields =
      config.sectionsByOption[selectedOption]?.flatMap((section) =>
        section.fields.map((field) => field.key || sanitizeKey(field.label)),
      ) || [];
    const isCompleted = allFields.every(
      (fieldKey) => currentFormData[fieldKey]?.trim() !== '',
    );
    setFormCompleted(isCompleted);
  }, [formData, formKey, config, selectedOption]);

  const handleFieldChange = useCallback(
    (fieldKey: string, value: string) => {
      updateField(formKey, fieldKey, value);
      checkFormCompletion();
    },
    [updateField, formKey, checkFormCompletion],
  );

  const handleFieldBlur = useCallback(
    (fieldKey: string, value: string) => {
      updateField(formKey, fieldKey, value, false);
      checkFormCompletion();
    },
    [updateField, formKey, checkFormCompletion],
  );

  const handleSelectChange = useCallback(
    (fieldKey = '', value: string) => {
      updateField(formKey, fieldKey, value, true);
      checkFormCompletion();
    },
    [updateField, formKey, checkFormCompletion],
  );

  const getFieldValue = useCallback(
    (fieldKey: string) => {
      let value = '';
      const [section, key] = fieldKey.split('.');
      if (
        basin &&
        basin !== 'user-defined' &&
        iniJsonData[basin][section]?.[key]
      ) {
        if (Object.entries(iniJsonData[basin]).length > 0) {
          value = iniJsonData[basin][section][key].split('|')[0].trim();
          return value;
        }
      }

      if (
        liquefaction &&
        Object.entries(iniJsonData['liquefaction_facilities']).length > 0 &&
        iniJsonData['liquefaction_facilities'][liquefaction][key]
      ) {
        value = iniJsonData['liquefaction_facilities'][liquefaction][key]
          .split('|')[0]
          .trim();
        return value;
      }

      if (value === '' && formData[formKey]?.[fieldKey]) {
        return formData[formKey][fieldKey];
      }

      return '';
    },
    [basin, liquefaction, iniJsonData, formData, formKey],
  );

  const memoizedFieldValue = useMemo(() => {
    return (fieldKey: string) => getFieldValue(fieldKey);
  }, [getFieldValue]);

  useEffect(() => {
    checkFormCompletion();
  }, [formData, selectedOption]);

  return (
    <div
      style={{ padding: '10px', fontFamily: 'Arial, sans-serif' }}
      hidden={hide}
    >
      <Typography variant="h5" sx={{ marginBottom: '20px' }}>
        {config.title}
      </Typography>
      {config.options.length > 0 && (
        <FormControl fullWidth margin="dense">
          <InputLabel>Select Section*</InputLabel>
          <Select
            value={selectedOption}
            onChange={(e) => handleSelectChange(config.key, e.target.value)}
            name={config.key}
            size="small"
          >
            {config.options.map((option) => {
              const optionKey =
                typeof option === 'string' ? option : option.key;
              const optionLabel =
                typeof option === 'string' ? option : option.label;
              const optionValue =
                typeof option === 'string' ? option : option.value;
              return (
                <MenuItem key={optionKey} value={optionValue}>
                  {optionLabel}
                </MenuItem>
              );
            })}
          </Select>
        </FormControl>
      )}
      {config.sectionsByOption[selectedOption]?.map((section) => (
        <div key={section.title} style={{ marginBottom: '15px' }}>
          <Typography variant="h6" gutterBottom>
            {section.title}
          </Typography>
          <Grid container spacing={1}>
            {section.fields.map((field) => {
              const fieldKey = field.key || sanitizeKey(field.label);
              return (
                <Grid container xs={12} sm={12} key={field.label}>
                  <Grid item xs={4} sm={4} padding={1}>
                    {field.label}
                    {field.unit && field.unit !== '' ? `, ${field.unit}` : ''}
                  </Grid>
                  <Grid item xs={8} sm={8} padding={1}>
                    {field.type === 'text' && (
                      <TextField
                        fullWidth
                        label={field.label}
                        name={field.key}
                        value={memoizedFieldValue(fieldKey)}
                        onChange={(e) =>
                          handleFieldChange(fieldKey, e.target.value)
                        }
                        onBlur={(e) =>
                          handleFieldBlur(fieldKey, e.target.value)
                        }
                        size="small"
                        disabled={field.readonly}
                      />
                    )}
                    {field.type === 'select' && (
                      <FormControl fullWidth size="small">
                        <InputLabel>
                          {field.label}
                          {field.unit && field.unit !== ''
                            ? `, ${field.unit}`
                            : ''}
                        </InputLabel>
                        <Select
                          value={getFieldValue(fieldKey)}
                          name={field.key}
                          onChange={(e) =>
                            handleFieldChange(fieldKey, e.target.value)
                          }
                          disabled={field.readonly}
                        >
                          {field.options?.map((option) => {
                            if (typeof option === 'string') {
                              return (
                                <MenuItem key={option} value={option}>
                                  {option}
                                </MenuItem>
                              );
                            } else {
                              return (
                                <MenuItem key={option.key} value={option.value}>
                                  {option.label}
                                </MenuItem>
                              );
                            }
                          })}
                        </Select>
                      </FormControl>
                    )}
                  </Grid>
                </Grid>
              );
            })}
          </Grid>
        </div>
      ))}
    </div>
  );
};

export default UniversalForm;
